import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LabelInput, LabelError } from 'shared/components/Labels';
import { PasswordField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import MainSpinner from 'shared/components/MainSpinner';
import AlertModal from 'shared/components/AlertModal';

import useApi from 'hooks/useApi';
import { closeAlert, showAlert } from 'redux/actions/alertActions';

import { convertErrorsArrayToString } from 'shared/helpers/helper';
import { ALERT_TYPE_ERROR } from 'shared/utils';

import { ResetPasswordTitle } from '../styles';

const ResetPasswordForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();
  const dispatch = useDispatch();
  const { idCustomer } = useSelector((state) => state.auth);
  const { openAlert, alertType, alertMessage } = useSelector((state) => state.alertState);
  const { customAreaApi } = useApi();
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm();
  const [token, setToken] = useState();
  const [admin, setAdmin] = useState();
  const [login, setLogin] = useState();
  const [action, setAction] = useState(null);
  const [idClient, setIdClient] = useState();

  useEffect(() => {
    if (search && search.length) {
      const params = new URLSearchParams(search);
      const tokenParam = params.get('token');
      const adminParam = params.get('admin');
      const loginParam = params.get('login');
      const actionParam = params.get('action');
      const idClientParam = params.get('idClient');
      setToken(tokenParam);
      setAdmin(adminParam);
      setLogin(loginParam);
      setAction(actionParam);
      setIdClient(idClientParam);
    }
  }, [search]);
  const [{ loading }, resetPassword] = customAreaApi(
    {
      url: `${window.API_V1}/login/pass/reset`,
      method: 'POST',
    },
    { manual: true }
  );
  const onCloseAlert = () => dispatch(closeAlert());
  const onSubmit = async (values) => {
    try {
      if (values && token) {
        const { password } = values;
        const {
          data: { errors: resetPasswordErrors },
        } = await resetPassword({
          data: {
            login,
            newPass: password,
            token,
            idClient,
            idCustomer: parseInt(idCustomer, 10),
            admin,
          },
        });
        if (resetPasswordErrors.length) {
          throw new Error(convertErrorsArrayToString(resetPasswordErrors));
        }
        history.push('/');
      }
    } catch (ex) {
      console.error('error calling reset password endpoint: ', ex);
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
    }
  };
  return (
    <>
      {!action && <ResetPasswordTitle>{t('reset_password_title')}</ResetPasswordTitle>}
      {action === 'resendaccess' && (
        <ResetPasswordTitle>{t('reset_password_title')}</ResetPasswordTitle>
      )}
      {action === 'addaccess' && <ResetPasswordTitle>{t('set_password_title')}</ResetPasswordTitle>}
      <form className="form reset-password-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="form__form-group">
          <LabelInput
            className="form__form-group-label"
            label={t('reset_password_new_password')}
            error={errors.password != null}
          />
          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={{
              required: {
                value: true,
                message: t('general_required_field'),
              },
              minLength: {
                value: 8,
                message: t('reset_password_invalid_lenght_password'),
              },
            }}
            render={({ field }) => (
              <PasswordField
                borderRadius={2}
                value={field.value}
                onChange={field.onChange}
                error={errors.password !== undefined}
              />
            )}
          />
          {errors.password ? (
            <LabelError label={errors.password.message} />
          ) : (
            <LabelError
              className="helper-text"
              label={t('reset_password_invalid_lenght_password')}
            />
          )}
        </div>
        <div className="form__form-group">
          <LabelInput
            className="form__form-group-label"
            label={t('reset_password_confirm_new_password')}
            error={errors.confirmPassword != null}
          />
          <Controller
            name="confirmPassword"
            control={control}
            defaultValue=""
            rules={{
              required: {
                value: true,
                message: t('general_required_field'),
              },
              validate: {
                passwordEqual: (value) =>
                  value === getValues().password || t('reset_password_invalid_match_password'),
              },
            }}
            render={({ field }) => (
              <PasswordField
                borderRadius={2}
                value={field.value}
                onChange={field.onChange}
                error={errors.confirmPassword !== undefined}
              />
            )}
          />
          {errors.confirmPassword && <LabelError label={errors.confirmPassword.message} />}
        </div>
        <Button
          className="text-uppercase submit-button"
          type="submit"
          label={t('reset_password_save_password')}
          color="primary"
        />
        {loading && <MainSpinner />}
        <AlertModal
          type={alertType}
          isOpen={openAlert}
          toggle={onCloseAlert}
          message={alertMessage}
          okText={t('general_button_ok')}
          onOk={onCloseAlert}
        />
      </form>
    </>
  );
};

export default ResetPasswordForm;
